/**
 * TRENDYOL UI ENHANCEMENTS
 * Minimal JavaScript for UI animations and interactions only
 * NO form processing - relies entirely on native Shopify facets.js
 * Replaces the complex 900+ line synchronization system
 */

class TrendyolUIEnhancements {
  constructor() {
    this.mobileDrawer = null;
    this.mobileOverlay = null;
    this.isInitialized = false;
    
    this.init();
  }

  init() {
    if (this.isInitialized) return;
    
    try {
      this.setupMobileDrawer();
      this.setupFilterSearch();
      this.setupActiveFilterCount();
      this.setupCategoryAnimations();
      this.setupSidebarPositioning();
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing TrendyolUIEnhancements:', error);
    }
  }

  /**
   * Dynamic sidebar positioning based on actual header height
   * Uses JavaScript scroll handling since CSS sticky doesn't work with fixed header
   */
  setupSidebarPositioning() {
    const sidebar = document.querySelector('.trendyol-sidebar');
    const header = document.querySelector('.clean-header');
    
    if (!sidebar || !header) return;

    // Get the sidebar's original parent for reference
    const sidebarParent = sidebar.parentElement;
    const layout = document.querySelector('.trendyol-facets-layout');
    const productGrid = document.querySelector('.product-grid-container');
    
    if (!layout || !productGrid) return;

    let headerHeight = 222; // Default fallback
    let sidebarOriginalTop = 0;
    let layoutTop = 0;
    let isFixed = false;

    const updateMeasurements = () => {
      headerHeight = header.getBoundingClientRect().height;
      
      // Get layout position relative to document
      const layoutRect = layout.getBoundingClientRect();
      layoutTop = layoutRect.top + window.scrollY;
      
      // Calculate sidebar height
      const viewportHeight = window.innerHeight;
      const maxSidebarHeight = viewportHeight - headerHeight;
      
      sidebar.style.maxHeight = `${maxSidebarHeight}px`;
    };

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const productGridRect = productGrid.getBoundingClientRect();
      const productGridBottom = productGridRect.bottom + scrollY;
      const sidebarHeight = sidebar.offsetHeight;
      
      // Calculate when sidebar should become fixed
      const stickyStartPoint = layoutTop - headerHeight;
      
      // Calculate when sidebar should stop (when it would go past product grid)
      const stickyEndPoint = productGridBottom - sidebarHeight - headerHeight;
      
      if (scrollY >= stickyStartPoint && scrollY < stickyEndPoint) {
        // Sidebar should be fixed
        if (!isFixed) {
          sidebar.style.position = 'fixed';
          sidebar.style.top = `${headerHeight}px`;
          sidebar.style.width = '280px';
          isFixed = true;
        }
      } else if (scrollY >= stickyEndPoint) {
        // Sidebar should be absolute at bottom
        sidebar.style.position = 'absolute';
        sidebar.style.top = `${stickyEndPoint - layoutTop + headerHeight}px`;
        sidebar.style.width = '280px';
        isFixed = false;
      } else {
        // Sidebar should be in normal flow
        if (isFixed || sidebar.style.position === 'absolute') {
          sidebar.style.position = 'sticky';
          sidebar.style.top = `${headerHeight}px`;
          sidebar.style.width = '';
          isFixed = false;
        }
      }
    };

    // Initial setup
    updateMeasurements();
    
    // Use requestAnimationFrame for smooth scroll handling
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Update on resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateMeasurements();
        handleScroll();
      }, 100);
    });

    // Update after fonts load
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        updateMeasurements();
        handleScroll();
      });
    }
    
    // Initial call
    handleScroll();
  }

  setupMobileDrawer() {
    this.mobileDrawer = document.querySelector('.trendyol-mobile-drawer');
    this.mobileOverlay = document.querySelector('.trendyol-mobile-overlay');
    
    // Mobile trigger button
    const mobileTrigger = document.querySelector('#trendyol-mobile-trigger');
    if (mobileTrigger) {
      mobileTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openMobileDrawer();
      });
    }

    // Mobile close button
    const mobileClose = document.querySelector('.trendyol-mobile-header__close');
    if (mobileClose) {
      mobileClose.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeMobileDrawer();
      });
    }

    // Overlay click to close
    if (this.mobileOverlay) {
      this.mobileOverlay.addEventListener('click', (e) => {
        if (e.target === this.mobileOverlay) {
          this.closeMobileDrawer();
        }
      });
    }

    // Handle native menu-drawer behavior if using menu-drawer element
    const menuDrawer = document.querySelector('menu-drawer.trendyol-mobile-overlay');
    if (menuDrawer && menuDrawer.bindEvents) {
      menuDrawer.bindEvents();
    }
  }

  openMobileDrawer() {
    if (this.mobileOverlay && this.mobileDrawer) {
      this.mobileOverlay.classList.add('is-active');
      this.mobileDrawer.classList.add('is-active');
      document.body.classList.add('trendyol-mobile-open');
      
      // If using details element, open it
      const details = this.mobileDrawer.querySelector('details');
      if (details) {
        details.open = true;
      }
    }
  }

  closeMobileDrawer() {
    if (this.mobileOverlay && this.mobileDrawer) {
      this.mobileOverlay.classList.remove('is-active');
      this.mobileDrawer.classList.remove('is-active');
      document.body.classList.remove('trendyol-mobile-open');
      
      // If using details element, close it
      const details = this.mobileDrawer.querySelector('details');
      if (details) {
        details.open = false;
      }
    }
  }

  setupFilterSearch() {
    // Desktop search
    const searchInputs = document.querySelectorAll('.trendyol-search__input');
    searchInputs.forEach(input => {
      input.addEventListener('input', (e) => {
        this.handleFilterSearch(e.target);
      });
    });

    // Mobile search
    const mobileSearchInputs = document.querySelectorAll('.trendyol-mobile-search__input');
    mobileSearchInputs.forEach(input => {
      input.addEventListener('input', (e) => {
        this.handleFilterSearch(e.target, true);
      });
    });
  }

  handleFilterSearch(input, isMobile = false) {
    const searchTerm = input.value.toLowerCase();
    const category = input.closest('.trendyol-filter-category, .trendyol-mobile-category');
    
    if (!category) return;

    const optionSelector = isMobile 
      ? '.mobile-facets__item, .trendyol-mobile-checkbox' 
      : '.list-menu__item, .trendyol-filter-option, .trendyol-checkbox';
    
    const labelSelector = isMobile
      ? '.mobile-facets__label-text, .trendyol-mobile-checkbox__label'
      : '.facets__label-text, .trendyol-checkbox__label';

    const options = category.querySelectorAll(optionSelector);
    options.forEach(option => {
      const label = option.querySelector(labelSelector);
      const text = label ? label.textContent.toLowerCase() : '';
      
      if (text.includes(searchTerm)) {
        option.style.display = '';
      } else {
        option.style.display = 'none';
      }
    });
  }

  setupActiveFilterCount() {
    // Update filter count on page load
    this.updateActiveFilterCount();

    // Listen to native facet changes to update count
    document.addEventListener('facets:updated', () => {
      setTimeout(() => this.updateActiveFilterCount(), 100);
    });

    // Also listen for form changes as backup
    const forms = document.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormMobile');
    forms.forEach(form => {
      form.addEventListener('change', () => {
        setTimeout(() => this.updateActiveFilterCount(), 100);
      });
    });
  }

  updateActiveFilterCount() {
    // Count active filters from the native system
    const activeDesktopFilters = document.querySelectorAll('#FacetFiltersForm input:checked, #FacetFiltersForm input[type="number"][value]:not([value=""])');
    const activeMobileFilters = document.querySelectorAll('#FacetFiltersFormMobile input:checked, #FacetFiltersFormMobile input[type="number"][value]:not([value=""])');
    
    // Use desktop count as primary (mobile should mirror it)
    let count = activeDesktopFilters.length;
    
    // Update count displays
    const countElements = document.querySelectorAll('#trendyol-filter-count, .trendyol-mobile-trigger__count');
    countElements.forEach(element => {
      element.textContent = count;
      element.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  setupCategoryAnimations() {
    // Desktop category toggles - these work with native details/summary
    const categoryHeaders = document.querySelectorAll('.trendyol-filter-category__header');
    categoryHeaders.forEach(header => {
      // Let native details/summary handle the toggle
      // Just add visual feedback
      header.addEventListener('click', () => {
        const category = header.closest('.trendyol-filter-category');
        if (category) {
          // Add a small delay for the animation effect
          setTimeout(() => {
            category.classList.toggle('is-animating');
            setTimeout(() => {
              category.classList.remove('is-animating');
            }, 300);
          }, 10);
        }
      });
    });

    // Mobile category toggles - these also work with native details/summary
    const mobileCategoryHeaders = document.querySelectorAll('.trendyol-mobile-category__header');
    mobileCategoryHeaders.forEach(header => {
      header.addEventListener('click', () => {
        const category = header.closest('.trendyol-mobile-category');
        if (category) {
          setTimeout(() => {
            category.classList.toggle('is-animating');
            setTimeout(() => {
              category.classList.remove('is-animating');
            }, 300);
          }, 10);
        }
      });
    });
  }

  // Public method to refresh UI elements
  refresh() {
    this.updateActiveFilterCount();
    this.setupFilterSearch();
    this.setupCategoryAnimations();
  }

  // Public method to get current filter state (read-only)
  getFilterState() {
    const activeFilters = document.querySelectorAll('#FacetFiltersForm input:checked');
    const priceInputs = document.querySelectorAll('#FacetFiltersForm input[type="number"]');
    
    const state = {
      totalActive: activeFilters.length,
      priceRange: {
        min: null,
        max: null
      },
      filters: []
    };

    activeFilters.forEach(input => {
      state.filters.push({
        name: input.name,
        value: input.value
      });
    });

    priceInputs.forEach(input => {
      if (input.value) {
        if (input.name.includes('min') || input.name.includes('gte')) {
          state.priceRange.min = input.value;
        } else if (input.name.includes('max') || input.name.includes('lte')) {
          state.priceRange.max = input.value;
        }
      }
    });

    return state;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Small delay to ensure native facets loads first
  setTimeout(() => {
    // Only initialize if trendyol elements are present
    if (document.querySelector('.trendyol-sidebar, #trendyol-mobile-trigger')) {
      window.trendyolUI = new TrendyolUIEnhancements();
    }
  }, 100);
});

// Reinitialize after section updates
document.addEventListener('shopify:section:load', () => {
  setTimeout(() => {
    if (document.querySelector('.trendyol-sidebar, #trendyol-mobile-trigger')) {
      if (window.trendyolUI) {
        window.trendyolUI.refresh();
      } else {
        window.trendyolUI = new TrendyolUIEnhancements();
      }
    }
  }, 100);
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = TrendyolUIEnhancements;
}